import { IPOSProductPreviewDetails } from '../../pages/Printshop/POSCreator/POSCreator';
import { CurrencyType } from '../Product/ProductPriceHelper';
import { POSPromotionTypes } from './POSPromotionTypes.enum';
import { UEPMeasure } from './UEPMeasure.enum';

export const promotionIncludesPrice = (promotionType: POSPromotionTypes) => {
  return [
    POSPromotionTypes.No_Strikethrough,
    POSPromotionTypes.Strikethrough,
    POSPromotionTypes.Multibuy,
    POSPromotionTypes.Better_Than_Half_Price,
    POSPromotionTypes.Half_Price,
    POSPromotionTypes.Save_a_Third,
    POSPromotionTypes.BOGOF,
  ].includes(promotionType);
};

export const promotionIncludesPrevPrice = (promotionType: POSPromotionTypes) => {
  return [
    POSPromotionTypes.Strikethrough,
    POSPromotionTypes.Half_Price,
    POSPromotionTypes.Better_Than_Half_Price,
    POSPromotionTypes.Save_a_Third,
    POSPromotionTypes.BOGOF,
  ].includes(promotionType);
};

export const promotionHasPriceAtTheBottom = (promotionType: POSPromotionTypes) => {
  return [
    POSPromotionTypes.Better_Than_Half_Price,
    POSPromotionTypes.Half_Price,
    POSPromotionTypes.BOGOF,
    POSPromotionTypes.Save_a_Third,
  ].includes(promotionType);
};

export const convertToCurrency = (price: number, currency: CurrencyType) => {
  if (currency === CurrencyType.Pence) {
    return price * 100;
  }
  return price / 100;
};

export const getUEPFieldsForPromotion = (promotionType: POSPromotionTypes): string[] => {
  switch (promotionType) {
    case POSPromotionTypes.No_Strikethrough:
      return ['uepPrice'];
    case POSPromotionTypes.Strikethrough:
      return ['uepPrice'];
    case POSPromotionTypes.Multibuy:
      return ['uepPriceEach', 'uepMultibuyPrice'];
    case POSPromotionTypes.Half_Price:
      return ['uepHalfPrice'];
    case POSPromotionTypes.Better_Than_Half_Price:
      return ['uepPrice'];
    case POSPromotionTypes.BOGOF:
      return ['uepPriceEach'];
    case POSPromotionTypes.Save_a_Third:
      return ['uepPrice'];
    case POSPromotionTypes.POS_General:
      return [];
    default:
      return [];
  }
};

export const validateUEPFields = (product: IPOSProductPreviewDetails): boolean => {
  if (product.promotionType === POSPromotionTypes.POS_General) {
    return true;
  }

  if (product.uepMeasure === undefined || product.uepMeasure === null) {
    return false;
  }

  const requiredFields = getUEPFieldsForPromotion(product.promotionType);

  for (const fieldName of requiredFields) {
    const value = product[fieldName as keyof IPOSProductPreviewDetails];

    if (!value || value === '') {
      return false;
    }

    if (typeof value === 'string') {
      const priceRegex = /^\d{1,5}(\.\d{1,2})?$/;
      if (!priceRegex.test(value)) {
        return false;
      }
    }
  }

  return true;
};

export const validateProducts = (products: IPOSProductPreviewDetails[]): boolean => {
  let result = true;

  products.forEach((product) => {
    const {
      description,
      promotionType,
      price,
      prevPrice,
      multibuyValue,
      priceCurrency,
      prevPriceCurrency,
      maxAllowedPrice,
    } = product;

    let productPrice = parseFloat(price);
    const productPrevPrice = parseFloat(prevPrice);

    if (!description) {
      result = false;
    }

    if (promotionIncludesPrice(promotionType) && (!productPrice || productPrice > maxAllowedPrice)) {
      result = false;
    }

    if (priceCurrency !== prevPriceCurrency) {
      productPrice = convertToCurrency(productPrice, prevPriceCurrency);
    }

    if (promotionIncludesPrevPrice(promotionType) && (!productPrevPrice || productPrevPrice <= productPrice)) {
      result = false;
    }

    if (promotionType === POSPromotionTypes.Multibuy && !parseFloat(multibuyValue)) {
      result = false;
    }

    if (!validateUEPFields(product)) {
      result = false;
    }
  });

  return result;
};

export const formatUEPWeight = (uepContentsValue: number, uepContentsUOM: string): string => {
  if (!uepContentsValue || uepContentsValue <= 0) {
    return '';
  }

  const uom = (uepContentsUOM || '').toUpperCase();

  if (uom === 'KG') {
    if (uepContentsValue >= 1) {
      return `${uepContentsValue}kg`;
    }
    const grams = uepContentsValue * 1000;
    if (grams >= 1) {
      return `${parseFloat(grams.toFixed(2))}g`;
    }
    const mg = uepContentsValue * 1000000;
    return `${parseFloat(mg.toFixed(2))}mg`;
  }

  if (uom === 'L') {
    if (uepContentsValue >= 1) {
      return `${uepContentsValue}l`;
    }
    if (uepContentsValue > 0.5) {
      const cl = uepContentsValue * 100;
      return `${parseFloat(cl.toFixed(2))}cl`;
    }
    const ml = uepContentsValue * 1000;
    return `${parseFloat(ml.toFixed(2))}ml`;
  }

  if (uom === 'EACH') {
    return `${uepContentsValue.toFixed(2)} EACH`;
  }

  return '';
};

const uepDivide = (price: number, uepValue: number): string | undefined => {
  if (price > 0 && uepValue > 0) {
    return (price / uepValue).toFixed(2);
  }
  return undefined;
};

export const recalculateUEP = (product: IPOSProductPreviewDetails): IPOSProductPreviewDetails => {
  const uepValue = product.uepContentsValue || 0;
  const canCalculate = uepValue > 0 && (product.uepMeasure !== UEPMeasure.Each || uepValue > 1);

  if (!canCalculate) {
    return product;
  }

  const price = parseFloat(product.price) || 0;
  const multibuyTotal = parseFloat(product.multibuyValue) || 0;
  const multiBuyCount = product.multiBuyProductCount || 1;
  const multibuyPerItem = multiBuyCount > 0 ? multibuyTotal / multiBuyCount : 0;

  return {
    ...product,
    uepPrice: uepDivide(price, uepValue),
    uepPriceEach: uepDivide(price, uepValue),
    uepHalfPrice: uepDivide(price, uepValue),
    uepMultibuyPrice: uepDivide(multibuyPerItem, uepValue),
  };
};
